import type { ArchiveRegisterEntry } from './archiveRegistry'
import { formatDate } from './formatters'

function downloadText(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType })
  const href = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = href
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.setTimeout(() => URL.revokeObjectURL(href), 1000)
}

function escapeHtml(value: string) {
  return value
    .split('&').join('&amp;')
    .split('<').join('&lt;')
    .split('>').join('&gt;')
    .split('"').join('&quot;')
    .split("'").join('&#39;')
}

function csvCell(value: string | number | null | undefined) {
  const text = value == null ? '' : String(value)
  return `"${text.split('"').join('""')}"`
}

function slugDate(date = new Date()) {
  return date.toISOString().slice(0, 10)
}

function monthKey(isoDate: string) {
  return isoDate.slice(0, 7)
}

export function downloadArchiveRegisterCsv(entries: ArchiveRegisterEntry[]) {
  const rows = [
    [
      'Generated At',
      'Job Number',
      'Project',
      'Request Type',
      'Request Label',
      'Profile',
      'Filename',
      'Archive Ready Pct',
      'Outputs',
      'PO Count',
      'Variation Count',
      'Claim Count',
      'Permit Count',
      'Inspection Count',
      'SWMS Count',
      'Site Diary Count',
      'Incident Count',
      'NCR Count',
    ],
    ...entries.map((entry) => [
      entry.generatedAt,
      entry.jobNumber,
      entry.jobDescription,
      entry.requestType,
      entry.requestLabel,
      entry.profileLabel ?? '',
      entry.filename,
      entry.archiveReadyPct ?? '',
      entry.outputs.join(' | '),
      entry.counts.purchaseOrders,
      entry.counts.variations,
      entry.counts.progressClaims,
      entry.counts.permits,
      entry.counts.inspections,
      entry.counts.swms,
      entry.counts.siteDiaries,
      entry.counts.incidents,
      entry.counts.ncrs,
    ]),
  ]

  const csv = rows.map((row) => row.map((cell) => csvCell(cell)).join(',')).join('\n')
  const filename = `Archive_Register_${slugDate()}.csv`
  downloadText(csv, filename, 'text/csv;charset=utf-8')
  return filename
}

export function downloadArchiveManifestJson(entries: ArchiveRegisterEntry[]) {
  const generatedAt = new Date().toISOString()
  const byType = entries.reduce<Record<string, number>>((acc, entry) => {
    acc[entry.requestType] = (acc[entry.requestType] ?? 0) + 1
    return acc
  }, {})
  const byMonth = entries.reduce<Record<string, number>>((acc, entry) => {
    const key = monthKey(entry.generatedAt)
    acc[key] = (acc[key] ?? 0) + 1
    return acc
  }, {})

  const manifest = {
    generatedAt,
    totalEntries: entries.length,
    uniqueJobs: new Set(entries.map((entry) => entry.jobId)).size,
    byType,
    byMonth,
    entries,
  }

  const filename = `Archive_Manifest_${slugDate()}.json`
  downloadText(JSON.stringify(manifest, null, 2), filename, 'application/json;charset=utf-8')
  return filename
}

export function downloadArchiveSummaryHtml(entries: ArchiveRegisterEntry[]) {
  const generatedAt = new Date().toISOString()
  const uniqueJobs = new Set(entries.map((entry) => entry.jobId)).size
  const averageReady = entries.length > 0
    ? Math.round(entries.reduce((sum, entry) => sum + (entry.archiveReadyPct ?? 0), 0) / entries.length)
    : 0

  const byType = Object.entries(entries.reduce<Record<string, number>>((acc, entry) => {
    acc[entry.requestType] = (acc[entry.requestType] ?? 0) + 1
    return acc
  }, {}))

  const byMonth = Object.entries(entries.reduce<Record<string, number>>((acc, entry) => {
    const key = monthKey(entry.generatedAt)
    acc[key] = (acc[key] ?? 0) + 1
    return acc
  }, {})).sort((a, b) => a[0].localeCompare(b[0]))

  const rows = entries.map((entry) => `
    <tr>
      <td>${escapeHtml(formatDate(entry.generatedAt))}</td>
      <td>${escapeHtml(entry.jobNumber)}</td>
      <td>${escapeHtml(entry.jobDescription)}</td>
      <td>${escapeHtml(entry.requestType)}</td>
      <td>${escapeHtml(entry.requestLabel)}</td>
      <td>${escapeHtml(entry.profileLabel ?? '-')}</td>
      <td>${escapeHtml(entry.filename)}</td>
      <td>${escapeHtml(String(entry.archiveReadyPct ?? '-'))}</td>
    </tr>
  `).join('')

  const html = `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>Archive Register Summary</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 32px; color: #17212b; }
    h1, h2 { margin: 0 0 12px; }
    .grid { display: grid; gap: 16px; grid-template-columns: repeat(4, minmax(0, 1fr)); margin-bottom: 24px; }
    .card { border: 1px solid #d9e1e8; border-radius: 10px; padding: 16px; }
    .label { font-size: 12px; color: #64748b; text-transform: uppercase; }
    .value { font-size: 24px; font-weight: 700; margin-top: 8px; }
    .two-col { display: grid; gap: 24px; grid-template-columns: 1fr 1fr; margin-bottom: 24px; }
    table { width: 100%; border-collapse: collapse; }
    th, td { border: 1px solid #d9e1e8; padding: 8px; text-align: left; vertical-align: top; }
    th { background: #f8fafc; font-size: 12px; text-transform: uppercase; color: #475569; }
    ul { margin: 0; padding-left: 18px; }
  </style>
</head>
<body>
  <h1>Archive Register Summary</h1>
  <p>Generated ${escapeHtml(formatDate(generatedAt))}. This report summarises the current local archive register.</p>

  <div class="grid">
    <div class="card"><div class="label">Archive packs</div><div class="value">${entries.length}</div></div>
    <div class="card"><div class="label">Projects covered</div><div class="value">${uniqueJobs}</div></div>
    <div class="card"><div class="label">Average readiness</div><div class="value">${averageReady}%</div></div>
    <div class="card"><div class="label">Latest package</div><div class="value">${entries[0] ? escapeHtml(formatDate(entries[0].generatedAt)) : 'None'}</div></div>
  </div>

  <div class="two-col">
    <div>
      <h2>By package type</h2>
      <ul>${byType.map(([key, count]) => `<li>${escapeHtml(key)}: ${count}</li>`).join('')}</ul>
    </div>
    <div>
      <h2>By month</h2>
      <ul>${byMonth.map(([key, count]) => `<li>${escapeHtml(key)}: ${count}</li>`).join('')}</ul>
    </div>
  </div>

  <h2>Detailed register</h2>
  <table>
    <thead>
      <tr>
        <th>Generated</th>
        <th>Job</th>
        <th>Project</th>
        <th>Type</th>
        <th>Label</th>
        <th>Profile</th>
        <th>Filename</th>
        <th>Ready %</th>
      </tr>
    </thead>
    <tbody>${rows || '<tr><td colspan="8">No archive entries recorded.</td></tr>'}</tbody>
  </table>
</body>
</html>`

  const filename = `Archive_Register_Summary_${slugDate()}.html`
  downloadText(html, filename, 'text/html;charset=utf-8')
  return filename
}
